import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Download, X, Loader2, AlertCircle, CheckCircle2, Filter, Calendar,
  ListChecks, FileSpreadsheet, FileText, Search, Database, Users,
  ShieldCheck, AlertTriangle, Copy, Globe, ChevronDown, ChevronRight,
} from 'lucide-react';

const API_URL = (typeof window !== 'undefined' && window.location && window.location.origin)
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL;

const MODES = [
  { id: 'full', label: 'Full Database', icon: Database, desc: 'Every lead in the system. 162 columns × all rows.' },
  { id: 'current_page', label: 'Current Page', icon: FileText, desc: 'Just the rows currently visible in the leads list.' },
  { id: 'filtered', label: 'Filtered Results', icon: Filter, desc: 'Honor the filters applied on the leads list.' },
  { id: 'selected', label: 'Selected Rows', icon: ListChecks, desc: 'Only the rows you have ticked in the leads table.' },
  { id: 'date_range', label: 'Date Range', icon: Calendar, desc: 'Leads whose Won Date / Created Date falls in a window.' },
  { id: 'custom_columns', label: 'Custom Columns', icon: ShieldCheck, desc: 'Pick the columns you want — others stay blank in the same template.' },
  { id: 'verified_only', label: 'Only Verified', icon: CheckCircle2, desc: 'Leads with verification_status=Verified.' },
  { id: 'active_only', label: 'Only Active', icon: Users, desc: 'Leads whose Property Status = Active.' },
  { id: 'closed_won', label: 'Only Closed Won', icon: ShieldCheck, desc: 'Leads in the Closed Won stage.' },
  { id: 'ota_specific', label: 'OTA Specific', icon: Globe, desc: 'Leads connected to a specific OTA (by name).' },
  { id: 'failed_data', label: 'Failed Data', icon: AlertTriangle, desc: 'Leads with critical fields missing or broken.' },
  { id: 'duplicates', label: 'Duplicate Leads', icon: Copy, desc: 'Rows sharing phone / email / hotel name with another lead.' },
];

const Section = ({ title, columns, selected, onToggleAll, onToggleOne, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const all = columns.every((c) => selected.has(c));
  const some = !all && columns.some((c) => selected.has(c));
  return (
    <div className="border border-gray-200 rounded-lg">
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700"
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {title}
          <span className="text-xs text-gray-500">({columns.length})</span>
        </button>
        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={all}
            ref={(el) => el && (el.indeterminate = some)}
            onChange={(e) => onToggleAll(columns, e.target.checked)}
          />
          Select all
        </label>
      </div>
      {open && (
        <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-1 max-h-60 overflow-y-auto">
          {columns.map((col) => (
            <label key={col} className="flex items-center gap-2 text-xs text-gray-700 hover:bg-gray-50 px-2 py-1 rounded">
              <input
                type="checkbox"
                checked={selected.has(col)}
                onChange={(e) => onToggleOne(col, e.target.checked)}
              />
              <span className="truncate" title={col}>{col}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const LeadAdvancedExportModal = ({ isOpen, onClose, currentFilters, selectedIds }) => {
  const [mode, setMode] = useState('full');
  const [format, setFormat] = useState('excel');
  const [audit, setAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [resultSummary, setResultSummary] = useState(null);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateField, setDateField] = useState('lead_date');
  const [otaFilter, setOtaFilter] = useState('booking');

  const [sections, setSections] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [selectedCols, setSelectedCols] = useState(new Set());
  const [colSearch, setColSearch] = useState('');

  // Load column metadata
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/leads/export/columns`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        setSections(data.sections || []);
        setAllColumns(data.all_columns || []);
        setSelectedCols(new Set(data.all_columns || []));
      } catch (e) {
        console.error('column load failed', e);
      }
    })();
  }, [isOpen]);

  const buildPayload = useCallback(() => {
    const p = { mode, format, include_summary_sheet: true };
    if (mode === 'selected') p.lead_ids = selectedIds || [];
    if (mode === 'filtered' || mode === 'current_page') p.filters = currentFilters || {};
    if (mode === 'date_range') {
      p.date_from = dateFrom || null;
      p.date_to = dateTo || null;
      p.date_field = dateField;
    }
    if (mode === 'ota_specific') p.ota_filter = otaFilter;
    if (mode === 'custom_columns') p.columns = Array.from(selectedCols);
    return p;
  }, [mode, format, selectedIds, currentFilters, dateFrom, dateTo, dateField, otaFilter, selectedCols]);

  const handleAudit = async () => {
    try {
      setAuditing(true);
      setError('');
      setAudit(null);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/leads/export/audit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Audit failed');
      setAudit(data);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setAuditing(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setError('');
      setResultSummary(null);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/leads/export/advanced`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Export failed (${res.status})`);
      }
      const summaryHeader = res.headers.get('X-Export-Summary');
      const cd = res.headers.get('Content-Disposition') || '';
      const fname = (cd.match(/filename="(.+?)"/) || [])[1] || `leads_export.${format === 'excel' ? 'xlsx' : 'csv'}`;
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      if (summaryHeader) {
        try {
          setResultSummary(JSON.parse(summaryHeader));
        } catch {
          // ignore parse failures
        }
      }
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setExporting(false);
    }
  };

  const filteredSections = useMemo(() => {
    if (!colSearch.trim()) return sections;
    const q = colSearch.trim().toLowerCase();
    return sections
      .map((s) => ({ ...s, columns: s.columns.filter((c) => c.toLowerCase().includes(q)) }))
      .filter((s) => s.columns.length > 0);
  }, [sections, colSearch]);

  const toggleAll = (cols, checked) => {
    setSelectedCols((prev) => {
      const next = new Set(prev);
      cols.forEach((c) => (checked ? next.add(c) : next.delete(c)));
      return next;
    });
  };
  const toggleOne = (col, checked) => {
    setSelectedCols((prev) => {
      const next = new Set(prev);
      if (checked) next.add(col); else next.delete(col);
      return next;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="advanced-export-modal">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
              <FileSpreadsheet className="text-teal-600" size={22} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Advanced Lead Export</h2>
              <p className="text-xs text-gray-500">162 columns · 12 modes · audit + cleanup before download</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="adv-export-close">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Mode picker */}
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-2">1. Pick a mode</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {MODES.map((m) => {
                const Icon = m.icon;
                const active = mode === m.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => { setMode(m.id); setAudit(null); setResultSummary(null); }}
                    data-testid={`adv-export-mode-${m.id}`}
                    className={`p-3 text-left border-2 rounded-lg transition-all ${
                      active ? 'border-teal-600 bg-teal-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={16} className={active ? 'text-teal-600' : 'text-gray-500'} />
                      <span className="text-sm font-medium text-gray-800">{m.label}</span>
                    </div>
                    <p className="text-[11px] text-gray-500 leading-snug">{m.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mode-specific options */}
          {mode === 'date_range' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="adv-export-date-from" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="adv-export-date-to" />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Field</label>
                <select value={dateField} onChange={(e) => setDateField(e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm">
                  <option value="lead_date">Won Date</option>
                  <option value="created_at">Created At</option>
                </select>
              </div>
            </div>
          )}

          {mode === 'ota_specific' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">OTA name contains</label>
              <input value={otaFilter} onChange={(e) => setOtaFilter(e.target.value)} placeholder="e.g. Booking, MMT, Goibibo" className="w-full md:w-1/3 border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="adv-export-ota-filter" />
            </div>
          )}

          {mode === 'selected' && (
            <div className="text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
              {selectedIds && selectedIds.length > 0
                ? `${selectedIds.length} row${selectedIds.length === 1 ? '' : 's'} selected from the leads list.`
                : 'No rows are currently selected. Close this modal, tick the leads you want, and re-open.'}
            </div>
          )}

          {mode === 'custom_columns' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800">Pick the columns to populate</h3>
                <div className="flex items-center gap-2">
                  <button onClick={() => setSelectedCols(new Set(allColumns))} className="text-xs text-teal-600 hover:underline" data-testid="adv-export-cols-all">All 162</button>
                  <button onClick={() => setSelectedCols(new Set())} className="text-xs text-gray-500 hover:underline" data-testid="adv-export-cols-none">None</button>
                </div>
              </div>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={colSearch} onChange={(e) => setColSearch(e.target.value)} placeholder="Search columns…" className="w-full border border-gray-200 rounded-lg pl-8 pr-3 py-2 text-sm" />
              </div>
              <div className="space-y-2">
                {filteredSections.map((s, idx) => (
                  <Section key={s.section} title={s.section} columns={s.columns} selected={selectedCols} onToggleAll={toggleAll} onToggleOne={toggleOne} defaultOpen={idx < 2} />
                ))}
              </div>
              <div className="text-xs text-gray-500">
                {selectedCols.size} of {allColumns.length} columns selected. Output keeps the canonical 162-column order — un-checked columns export as blanks so the template stays comparable across exports.
              </div>
            </div>
          )}

          {/* Format toggle */}
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-gray-800">2. Format</h3>
            {['excel', 'csv'].map((f) => (
              <button
                key={f}
                onClick={() => setFormat(f)}
                data-testid={`adv-export-format-${f}`}
                className={`px-3 py-1.5 rounded-lg text-sm border ${
                  format === f ? 'bg-teal-600 text-white border-teal-600' : 'bg-white border-gray-200 text-gray-600'
                }`}
              >
                {f === 'excel' ? 'Excel (.xlsx)' : 'CSV'}
              </button>
            ))}
          </div>

          {/* Audit panel */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800">3. Audit before export (optional)</h3>
              <button onClick={handleAudit} disabled={auditing} className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-white" data-testid="adv-export-audit-btn">
                {auditing ? <Loader2 size={12} className="inline animate-spin" /> : 'Run audit'}
              </button>
            </div>
            {audit && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Metric label="Rows" value={audit.rows_to_export} />
                <Metric label="Columns" value={audit.columns} />
                <Metric label="Repaired" value={audit.blank_cells_repaired} tone="ok" />
                <Metric label="Blank after fix" value={audit.blank_cells_after_repair} />
              </div>
            )}
            {audit && audit.warnings_sample && audit.warnings_sample.length > 0 && (
              <ul className="mt-3 text-xs text-amber-700 list-disc pl-5 space-y-0.5">
                {audit.warnings_sample.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
          </div>

          {/* Result summary after export */}
          {resultSummary && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-2" data-testid="adv-export-result-summary">
              <div className="flex items-center gap-2 text-emerald-800 font-semibold">
                <CheckCircle2 size={16} /> Export ready
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Metric label="Rows exported" value={resultSummary.rows_exported} />
                <Metric label="Columns" value={`${resultSummary.columns_visible}/${resultSummary.columns_exported}`} />
                <Metric label="Repaired" value={resultSummary.blank_cells_repaired} tone="ok" />
                <Metric label="Skipped" value={resultSummary.rows_skipped} />
              </div>
              {resultSummary.warnings && resultSummary.warnings.length > 0 && (
                <details className="text-xs text-amber-700">
                  <summary>{resultSummary.warnings.length} warning(s)</summary>
                  <ul className="list-disc pl-5 mt-1 space-y-0.5">
                    {resultSummary.warnings.slice(0, 25).map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
              <AlertCircle size={16} className="mt-0.5" />
              <div>{error}</div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <div className="text-xs text-gray-500">
            {audit ? `${audit.rows_to_export} rows × 162 columns ready` : 'Run audit for a preview, or export directly.'}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg">Cancel</button>
            <button
              onClick={handleExport}
              disabled={exporting}
              data-testid="adv-export-download-btn"
              className="flex items-center gap-2 px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm font-medium"
            >
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {exporting ? 'Generating…' : 'Download'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Metric = ({ label, value, tone }) => {
  const toneClass = tone === 'ok' ? 'text-emerald-700' : 'text-gray-800';
  return (
    <div className="bg-white rounded-md px-3 py-2 border border-gray-100">
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`text-base font-semibold ${toneClass}`}>{value ?? '—'}</div>
    </div>
  );
};

export default LeadAdvancedExportModal;
